import { BOOLEAN_RADIO_INPUT_OPTIONS } from '@constants/boolean-radio-input-options';
import { MONTH_DAY_YEAR_HOUR_MINUTE_DATE_FORMAT } from "@constants/date-formats";
import { resources } from '@constants/hardcoded-strings';
import { LOGIN_HINT_EXPIRATION_TYPE_OPTIONS } from '@constants/login-hint-expiration-type-options';
import { ENFORCE_LOGIN_HINT_EXPIRATION_PROPERTY_TYPE_ID, OIDC_LOGINHINT_EXPIRATION_PROPERTY_TYPE_ID } from '@constants/oidc-login-hint';
import { DateInputFormGroup } from "@containers/common/date-input-form-group/date-input-form-group";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { RadioInputFormGroup } from "@containers/common/radio-input-form-group/radio-input-form-group";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { LabelWidthTypes } from '@enums/label-width-types';
import { LayoutTypes } from '@enums/layout-types';
import { LoginHintExpirationTypeTypes } from '@enums/login-hint-expiration-type-types';
import { SelectOption } from '@idw-types/common';
import { LoginHintInfo } from '@idw-types/common/login-hint-info';
import { loginHintExpirationValueToMs } from '@utils/login-hint/login-hint-utils';
import React, { useMemo } from 'react';

type LoginHintExpirationContainerProps = {
    isDisabled: boolean;
    loginHintInfo: LoginHintInfo;
    loginHintExpirationTypeError: React.ReactNode;
    loginHintExpirationValueError: React.ReactNode;
    updateProperties: (properties: Array<{ propertyTypeId: string; newValue: string; added: boolean; }>) => void;
}

export const LoginHintExpirationContainer = ({
    isDisabled,
    loginHintInfo,
    loginHintExpirationTypeError,
    loginHintExpirationValueError,
    updateProperties
}: LoginHintExpirationContainerProps) => {
    const loginHintResources = resources.pages.verificationServiceDetails.tabs.general.subTabs.advanced.sections.loginHint;


    const updateLoginHintExpiration = (enabled: boolean, type: string = null, value: string = null) => {
        updateProperties([
            { propertyTypeId: OIDC_LOGINHINT_EXPIRATION_PROPERTY_TYPE_ID, newValue: enabled ? JSON.stringify({ type, value }) : null, added: enabled },
            { propertyTypeId: ENFORCE_LOGIN_HINT_EXPIRATION_PROPERTY_TYPE_ID, newValue: enabled ? "true" : null, added: enabled },
        ]);
    }

    const onChangeExpireLoginHintInner = (newValue: string) => {
        const enabled = newValue === BOOLEAN_RADIO_INPUT_OPTIONS[0].value;
        updateLoginHintExpiration(enabled);
    }

    const handleExpirationTypeChange = (selectedOption: SelectOption) => {
        const expirationType = selectedOption.value as LoginHintExpirationTypeTypes;
        updateLoginHintExpiration(true, expirationType, null);
    };

    const handleExpirationDateTimeChange = (date: Date) => {
        const expirationDate = date.getTime().toString();
        updateLoginHintExpiration(true, loginHintInfo.loginHintExpirationType, expirationDate);
    };

    const handleLoginHintExpirationDurationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { loginHintExpirationType } = loginHintInfo;
        const duration = loginHintExpirationValueToMs(loginHintExpirationType, event.target.value);

        updateLoginHintExpiration(true, loginHintInfo.loginHintExpirationType, duration);
    }

    const loginHintExpirationTypeSelectedOption = useMemo(() => {
        if (loginHintInfo.isExpireLoginHintEnabled) {
            return LOGIN_HINT_EXPIRATION_TYPE_OPTIONS.find(option => option.value === loginHintInfo.loginHintExpirationType);
        }

        return null;
    }, [loginHintInfo.isExpireLoginHintEnabled, loginHintInfo.loginHintExpirationType,]);

    return (
        <div>
            <div>
                <RadioInputFormGroup
                    controlId={'expire-login-hint'}
                    automationId={'expire-login-hint'}
                    disabled={isDisabled}
                    isRequired={false}
                    label={loginHintResources.inputs.expireLoginHint.label}
                    layout={LayoutTypes.COLUMN}
                    labelWidth={LabelWidthTypes.LARGE}
                    value={loginHintInfo.isExpireLoginHintEnabled
                        ? BOOLEAN_RADIO_INPUT_OPTIONS[0].value
                        : BOOLEAN_RADIO_INPUT_OPTIONS[1].value
                    }
                    options={BOOLEAN_RADIO_INPUT_OPTIONS}
                    onChange={onChangeExpireLoginHintInner}
                />

                {loginHintInfo.isExpireLoginHintEnabled && (
                    <>
                        <SelectFormGroup
                            controlId={"login-hint-expiration-type-select"}
                            automationId={"login-hint-expiration-type-select"}
                            disabled={isDisabled}
                            label={loginHintResources.inputs.loginHintExpirationType.label}
                            ariaLabel={loginHintResources.inputs.loginHintExpirationType.label}
                            isClearable={false}
                            isSearchable={false}
                            labelWidth={LabelWidthTypes.LARGE}
                            layout={LayoutTypes.COLUMN}
                            options={LOGIN_HINT_EXPIRATION_TYPE_OPTIONS}
                            selectedOption={loginHintExpirationTypeSelectedOption}
                            isRequired={true}
                            onChange={handleExpirationTypeChange}
                        />
                        {loginHintExpirationTypeError}

                        {loginHintInfo?.loginHintExpirationType === LoginHintExpirationTypeTypes.DATE_AND_TIME && (
                            <>
                                <DateInputFormGroup
                                    controlId={"login-hint-expiration-date-and-time-date-input"}
                                    automationId={"login-hint-expiration-date-and-time-date-input"}
                                    labelWidth={LabelWidthTypes.LARGE}
                                    layout={LayoutTypes.COLUMN}
                                    label={loginHintResources.inputs.loginHintExpirationDate.label}
                                    ariaLabel={loginHintResources.inputs.loginHintExpirationDate.label}
                                    disabled={isDisabled}
                                    isRequired={true}
                                    minDate={new Date()}
                                    maxDate={new Date(2999, 11, 31)}
                                    dateFormat={MONTH_DAY_YEAR_HOUR_MINUTE_DATE_FORMAT}
                                    value={loginHintInfo?.loginHintExpirationDate}
                                    onChange={handleExpirationDateTimeChange}
                                    shouldCloseOnSelect={true}
                                    showMonthDropdown={false}
                                    showYearDropdown={true}
                                    showMonthYearPicker={false}
                                    showTimeSelect={true}
                                />
                                {loginHintExpirationValueError}
                            </>)

                        }

                        {loginHintInfo?.loginHintExpirationType === LoginHintExpirationTypeTypes.DURATION && (
                            <>
                                <InputFormGroup
                                    controlId={"login-hint-expiration-duration-date-input"}
                                    automationId={"login-hint-expiration-duration-date-input"}
                                    label={loginHintResources.inputs.loginHintDuration.label}
                                    ariaLabel={loginHintResources.inputs.loginHintDuration.label}
                                    onChange={handleLoginHintExpirationDurationChange}
                                    value={loginHintInfo.loginHintDuration}
                                    disabled={isDisabled}
                                    isRequired={true}
                                    layout={LayoutTypes.COLUMN}
                                    labelWidth={LabelWidthTypes.LARGE}
                                />

                                {loginHintExpirationValueError}
                            </>
                        )
                        }
                    </>
                )
                }
            </div>
        </div>
    );
};